import { useState } from "react";
import { P, FF } from "../../styles/tokens";
import { fmt } from "../../utils/format";
import { StatCard, Card, Label, Amount, Pill, EmptyState } from "../ui";

export default function MetasView({ metas, setMetas, ahorradoPorMeta, data, flujo, mobile, totalIng }) {
  const [confirmingId, setConfirmingId] = useState(null);
  const upd = (id, fields) => setMetas(metas.map(m => m.id === id ? { ...m, ...fields } : m));
  const del = (id) => setMetas(metas.filter(m => m.id !== id));
  const add = () => setMetas([...metas, { id: crypto.randomUUID(), nombre: "", objetivo: 0, icono: "⭐" }]);

  // Empty state: sin metas creadas todavía
  if (metas.length === 0) {
    return (
      <EmptyState
        icon="🎯"
        title="Aún no tienes metas"
        desc="Crea tu primera meta de ahorro y empezá a verla crecer cada mes."
        btnLabel="Crear mi primera meta"
        onBtn={add}
        token={P.ingresos}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 14, marginBottom: 24 }}>
        <StatCard icon="💰" label="Disponible este mes" value={Math.max(flujo, 0)} token={P.ahorro} compact={mobile} />
        <StatCard icon="🎯" label="Total metas" value={metas.reduce((s, m) => s + m.objetivo, 0)} token={P.fijos} compact={mobile} />
        <StatCard icon="🌱" label="Total ya ahorrado" value={Object.values(ahorradoPorMeta).reduce((s, v) => s + v, 0)} token={P.ingresos} compact={mobile} />
      </div>
      {metas.map(meta => {
        const acumulado = ahorradoPorMeta[meta.id] || 0;
        const pct = meta.objetivo > 0 ? Math.min((acumulado / meta.objetivo) * 100, 100) : 0;
        const falta = Math.max(meta.objetivo - acumulado, 0);
        const aportes = Object.values(data).map(m => (m.ahorroMetas || []).find(a => a.metaId === meta.id)?.monto || 0).filter(v => v > 0);
        const prom = aportes.length > 0 ? aportes.reduce((a, b) => a + b, 0) / aportes.length : 0;
        const mesesPara = prom > 0 && falta > 0 ? Math.ceil(falta / prom) : null;
        return (
          <Card key={meta.id} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 20 }}>
              <input aria-label="Icono de la meta" value={meta.icono} onChange={e => upd(meta.id, { icono: e.target.value })}
                style={{ width: 48, height: 48, borderRadius: 16, border: `1.5px solid ${P.ahorro.tint}`, background: P.ahorro.pastel, textAlign: "center", fontSize: 22, fontFamily: FF, outline: "none", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <input aria-label="Nombre de la meta" value={meta.nombre} onChange={e => upd(meta.id, { nombre: e.target.value })} placeholder="Nombre de la meta…"
                  style={{ border: `1.5px solid ${P.border}`, borderRadius: 12, background: P.bg, color: P.text, fontSize: 14, fontFamily: FF, outline: "none", padding: "9px 12px", fontWeight: 700, width: "100%", boxSizing: "border-box" }} />
              </div>
              {confirmingId === meta.id ? (
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#991B1B" }}>¿Seguro?</span>
                  <button onClick={() => { del(meta.id); setConfirmingId(null); }}
                    aria-label="Confirmar eliminar"
                    style={{ width: 44, height: 44, borderRadius: 12, border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</button>
                  <button onClick={() => setConfirmingId(null)}
                    aria-label="Cancelar"
                    style={{ width: 44, height: 44, borderRadius: 12, border: "1.5px solid #E5E7EB", background: "#F3F4F6", color: "#6B7280", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
                </div>
              ) : (
                <button onClick={() => setConfirmingId(meta.id)}
                  style={{ width: 44, height: 44, borderRadius: 12, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textXs, cursor: "pointer", fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div>
                <Label style={{ marginBottom: 8 }}>Meta total</Label>
                <div style={{ position: "relative", marginTop: 8 }}>
                  <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: P.textXs, fontSize: 13 }}>$</span>
                  <input aria-label="Monto objetivo de la meta" type="number" inputMode="decimal" step="any" min="0" value={meta.objetivo || ""} onChange={e => upd(meta.id, { objetivo: Number(e.target.value) })} placeholder="0"
                    style={{ border: `1.5px solid ${P.border}`, borderRadius: 12, background: P.bg, color: P.text, fontSize: 16, fontFamily: FF, outline: "none", padding: "9px 12px", paddingLeft: 24, fontWeight: 900, width: "100%", boxSizing: "border-box" }} />
                </div>
              </div>
              <div>
                <Label style={{ marginBottom: 8 }}>Acumulado</Label>
                <div style={{ background: P.ingresos.pastel, borderRadius: 12, padding: "11px 14px", marginTop: 8, border: `1.5px solid ${P.ingresos.tint}` }}>
                  <Amount size={18} token={P.ingresos}>{fmt(acumulado)}</Amount>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 900, color: P.ahorro.text, fontFamily: FF }}>{pct.toFixed(0)}%</span>
                {falta > 0 && <span style={{ fontSize: 11, color: P.textXs, fontFamily: FF }}>faltan {fmt(falta)}</span>}
                {pct >= 100 && <Pill label="✓ Meta alcanzada" token={P.ingresos} />}
              </div>
              {mesesPara && <span style={{ fontSize: 11, color: P.textXs, fontFamily: FF, background: P.bg, padding: "4px 12px", borderRadius: 99, border: `1.5px solid ${P.border}` }}>~{mesesPara} {mesesPara === 1 ? "mes" : "meses"}</span>}
            </div>
            <div style={{ height: 10, background: P.bg, borderRadius: 99, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", borderRadius: 99, background: `linear-gradient(90deg,${P.ahorro.mid},${P.ahorro.base})`, width: `${pct}%`, transition: "width .5s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, color: P.textXs, fontFamily: FF }}>{fmt(acumulado)}</span>
              <span style={{ fontSize: 11, color: P.textXs, fontFamily: FF }}>{fmt(meta.objetivo)}</span>
            </div>
          </Card>
        );
      })}
      <button onClick={add}
        style={{ width: "100%", border: `2px dashed ${P.borderMd}`, borderRadius: 20, background: "transparent", color: P.textSm, cursor: "pointer", padding: "18px", fontSize: 14, fontFamily: FF, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16 }}>
        + Nueva meta de ahorro
      </button>
      <Card style={{ display: "flex", gap: 16, alignItems: "flex-start", background: P.ingresos.pastel, border: `1.5px solid ${P.ingresos.tint}` }}>
        <span style={{ fontSize: 22, flexShrink: 0 }}>💡</span>
        <p style={{ fontSize: 13, color: P.textMd, fontFamily: FF, lineHeight: 1.7, margin: 0 }}>
          El 20% recomendado equivale a <strong style={{ fontWeight: 900, color: P.ahorro.text }}>{fmt(totalIng * 0.2)}</strong> al mes.
        </p>
      </Card>
    </div>
  );
}
